import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, CheckCircle2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

export default function SecureUploadLink() {
  const { token } = useParams();
  const [linkData, setLinkData] = useState(null);
  const [error, setError] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [form, setForm] = useState({ businessName: '', ownerName: '', email: '', phone: '' });
  const [claimed, setClaimed] = useState(false);
  const [uploadsComplete, setUploadsComplete] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Try rep-based link first, fall back to token-based
    fetch(`${API}/upload-links/validate-rep/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setLinkData(data))
      .catch(() => {
        // Fallback: token-based
        fetch(`${API}/upload-links/validate/${token}`)
          .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.error); }))
          .then(data => setLinkData(data))
          .catch(e => setError(e.message || 'This link is invalid or expired'));
      });
  }, [token]);

  const handleClaim = async (e) => {
    e.preventDefault();
    try {
      const endpoint = linkData.repId
        ? `${API}/upload-links/claim-rep/${linkData.repId}`
        : `${API}/upload-links/claim/${token}`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setClientId(data.clientId);
      setClaimed(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleFileUpload = async (files, category) => {
    if (!clientId || !files.length) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('clientId', clientId);
      formData.append('category', category);
      formData.append('uploadedBy', 'upload-link');
      await fetch(`${API}/documents/upload`, { method: 'POST', body: formData }).catch(() => {});
    }
    setUploading(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-8 max-w-md w-full text-center">
          <AlertCircle size={32} className="mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!linkData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  if (uploadsComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-8 max-w-md w-full text-center">
          <CheckCircle2 size={32} className="mx-auto text-green-600 mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Upload Complete!</h1>
          <p className="text-gray-500 text-sm">Your documents have been received. Your representative will review them shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-lg mx-auto space-y-6">
        <div className="text-center pt-8">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-lg">CI</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Secure Document Upload</h1>
          <p className="text-gray-500 text-sm mt-1">
            {linkData.repName ? `${linkData.repName} has requested documents from you` : 'Upload your documents securely'}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-2.5">
          <Shield size={14} className="text-green-600" />
          <span className="text-green-700 text-xs font-medium">256-bit encrypted · AWS secured</span>
        </div>

        {!claimed && (
          <form onSubmit={handleClaim} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900">Your Information</h2>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Business Name</label>
                <input type="text" required value={form.businessName} onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Your business name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Your Name</label>
                <input type="text" required value={form.ownerName} onChange={e => setForm(f => ({ ...f, ownerName: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Full name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="you@business.com" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Phone (optional)</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="(555) 123-4567" />
              </div>
            </div>
            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm py-3 rounded-xl transition-colors">
              Continue to Upload
            </button>
          </form>
        )}

        {claimed && clientId && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900">Upload Your Documents</h2>
            <p className="text-gray-400 text-xs">Upload bank statements, voided check, and ID below.</p>
            <div className="space-y-3">
              <DropZone label="🏦 Bank Statements (last 3 months)" category="bank_statements" onUpload={handleFileUpload} />
              <DropZone label="🪪 Driver's License / ID" category="drivers_license" onUpload={handleFileUpload} />
              <DropZone label="✅ Voided Check" category="voided_check" onUpload={handleFileUpload} />
            </div>
            <button onClick={() => setUploadsComplete(true)} disabled={uploading}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold text-sm py-3 rounded-xl transition-colors mt-4 disabled:opacity-50">
              {uploading ? 'Uploading...' : "I'm Done Uploading"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DropZone({ label, category, onUpload }) {
  const [files, setFiles] = useState([]);
  const handleDrop = (e) => { e.preventDefault(); addFiles(e.dataTransfer.files); };
  const addFiles = (fileList) => {
    const arr = Array.from(fileList);
    setFiles(prev => [...prev, ...arr]);
    onUpload(arr, category);
  };
  return (
    <div>
      <p className="text-xs font-medium text-gray-600 mb-2">{label}</p>
      <div
        onDrop={handleDrop} onDragOver={e => e.preventDefault()}
        className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-blue-300 transition-colors cursor-pointer relative"
      >
        <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => addFiles(e.target.files)} />
        {files.length === 0
          ? <p className="text-gray-400 text-xs">Drop files here or click to browse</p>
          : <p className="text-green-600 text-xs font-medium">{files.length} file(s) selected ✓</p>
        }
      </div>
    </div>
  );
}
